import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Loader2,
  ShoppingCart,
  Wallet,
  Tag,
  Check,
  X,
  RefreshCw,
  Info,
  CheckCircle2,
  History,
} from "lucide-react";

const ROLES = ["Registrant", "Tech", "Admin", "AuxBilling"];

const FIELDS = [
  { key: "FirstName", label: "First Name", required: true, col: 6 },
  { key: "LastName", label: "Last Name", required: true, col: 6 },
  { key: "OrganizationName", label: "Organization", required: false, col: 6 },
  { key: "JobTitle", label: "Job Title", required: false, col: 6 },
  { key: "Address1", label: "Address Line 1", required: true, col: 12 },
  { key: "Address2", label: "Address Line 2", required: false, col: 12 },
  { key: "City", label: "City", required: true, col: 4 },
  { key: "StateProvince", label: "State / Province", required: true, col: 4 },
  { key: "PostalCode", label: "Postal Code", required: true, col: 4 },
  { key: "Country", label: "Country (ISO-2)", required: true, col: 4, placeholder: "US" },
  { key: "Phone", label: "Phone", required: true, col: 4, placeholder: "+1.5555555555" },
  { key: "PhoneExt", label: "Phone Ext", required: false, col: 4 },
  { key: "Fax", label: "Fax", required: false, col: 6, placeholder: "+1.5555555556" },
  { key: "EmailAddress", label: "Email", required: true, col: 6, type: "email" },
];

const emptyContact = () => FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const validateContact = (c) => {
  for (const f of FIELDS) {
    if (f.required && !String(c[f.key] || "").trim()) return `Field "${f.label}" is required`;
  }
  if (c.Country && c.Country.length !== 2) return "Country must be ISO-2 (e.g., US)";
  if (c.Phone && !/^\+\d{1,3}\.\d{6,15}$/.test(c.Phone))
    return "Phone format: +CC.NNNNNNNNNN (e.g., +1.5555555555)";
  return null;
};

const COL_CLASS = { 4: "sm:col-span-4", 6: "sm:col-span-6", 12: "sm:col-span-12" };

const ContactForm = ({ value, onChange, idPrefix }) => (
  <div className="grid grid-cols-12 gap-3">
    {FIELDS.map((f) => (
      <div key={f.key} className={`col-span-12 ${COL_CLASS[f.col] || "sm:col-span-6"}`}>
        <Label htmlFor={`${idPrefix}-${f.key}`} className="text-xs uppercase tracking-wider">
          {f.label}
          {f.required && <span className="text-[var(--error)] ml-0.5">*</span>}
        </Label>
        <Input
          id={`${idPrefix}-${f.key}`}
          data-testid={`${idPrefix}-${f.key}`}
          type={f.type || "text"}
          value={value[f.key] || ""}
          onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
          placeholder={f.placeholder || ""}
          className="mono mt-1.5 rounded-sm h-9"
        />
      </div>
    ))}
  </div>
);

const extractRegisterPrice = (pricingResult, tld) => {
  // namecheap.users.getPricing returns nested ProductType > ProductCategory > Product > Price (array)
  // Be defensive parsing across xmltodict shapes
  if (!pricingResult || typeof pricingResult !== "object") return null;
  const findArr = (val) => (Array.isArray(val) ? val : val ? [val] : []);
  try {
    const ptypes = findArr(pricingResult.ProductType);
    for (const pt of ptypes) {
      const cats = findArr(pt.ProductCategory);
      for (const cat of cats) {
        const prods = findArr(cat.Product);
        for (const prod of prods) {
          const name = (prod.Name || prod["@Name"] || "").toLowerCase();
          if (name && name !== tld.toLowerCase()) continue;
          const prices = findArr(prod.Price);
          // Prefer Duration=1 register price
          let chosen = null;
          for (const p of prices) {
            const dur = String(p.Duration || p["@Duration"] || "");
            const durType = String(p.DurationType || p["@DurationType"] || "").toLowerCase();
            const price = p.Price || p["@Price"];
            const yourPrice = p.YourPrice || p["@YourPrice"];
            const regularPrice = p.RegularPrice || p["@RegularPrice"];
            if (dur === "1" && (durType === "year" || durType === "")) {
              chosen = {
                price: Number(yourPrice || price || regularPrice || 0),
                regular: Number(regularPrice || price || 0),
                currency: p.Currency || p["@Currency"] || pricingResult.Currency || "USD",
                duration: 1,
              };
              break;
            }
            if (!chosen) {
              chosen = {
                price: Number(yourPrice || price || regularPrice || 0),
                regular: Number(regularPrice || price || 0),
                currency: p.Currency || p["@Currency"] || pricingResult.Currency || "USD",
                duration: Number(dur) || 1,
              };
            }
          }
          if (chosen) return chosen;
        }
      }
    }
  } catch (_e) {
    return null;
  }
  return null;
};

const BuyDomain = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initial = (params.get("domain") || "").trim().toLowerCase();
  const [domain, setDomain] = useState(initial);
  const [years, setYears] = useState(1);
  const [nameservers, setNameservers] = useState("");
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null); // { Available, IsPremiumName, PremiumRegistrationPrice }
  const [pricing, setPricing] = useState(null); // { price, regular, currency, duration }
  const [pricingLoading, setPricingLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [syncAll, setSyncAll] = useState(true);
  const [contacts, setContacts] = useState({
    Registrant: emptyContact(),
    Tech: emptyContact(),
    Admin: emptyContact(),
    AuxBilling: emptyContact(),
  });
  const [buying, setBuying] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [prefillSource, setPrefillSource] = useState(null); // {domain, created}
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [ownedDomains, setOwnedDomains] = useState([]); // [{name, created}]
  const [domainsLoading, setDomainsLoading] = useState(false);

  // Domains to ignore when auto-picking the most recent contact (test/junk domains)
  const PREFILL_SKIP = new Set(["testbuy315076.xyz"]);

  // Fetch the user's owned domains (used by both auto-prefill and the dropdown)
  const fetchOwnedDomains = async () => {
    setDomainsLoading(true);
    try {
      const { data: list } = await api.get("/domains/list", { params: { page: 1, page_size: 100 } });
      const sorted = (list?.domains || [])
        .map((d) => ({
          name: String(d.Name || "").toLowerCase(),
          created: d.Created || "",
          createdMs: Date.parse(String(d.Created || "").replace(/-/g, "/")) || 0,
        }))
        .filter((d) => d.name && !PREFILL_SKIP.has(d.name))
        .sort((a, b) => b.createdMs - a.createdMs);
      setOwnedDomains(sorted);
      return sorted;
    } catch (_e) {
      setOwnedDomains([]);
      return [];
    } finally {
      setDomainsLoading(false);
    }
  };

  // Fetch WHOIS Registrant for a single domain and apply to the form
  const applyContactFromDomain = async (domainName) => {
    if (!domainName) return false;
    setPrefillLoading(true);
    try {
      const { data: cd } = await api.get(`/domains/contacts/${encodeURIComponent(domainName)}`);
      const reg = cd?.Registrant || {};
      if (!reg.FirstName && !reg.EmailAddress && !reg.LastName) {
        toast.error(`No contact data for ${domainName}`);
        return false;
      }
      const merged = { ...emptyContact(), ...reg };
      setContacts({ Registrant: merged, Tech: merged, Admin: merged, AuxBilling: merged });
      const found = ownedDomains.find((d) => d.name === domainName);
      setPrefillSource({ domain: domainName, created: found?.created || "" });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load WHOIS");
      return false;
    } finally {
      setPrefillLoading(false);
    }
  };

  const loadLastContact = async () => {
    setPrefillLoading(true);
    try {
      const domains = await fetchOwnedDomains();
      // Walk newest-first and pick the first one with a non-empty Registrant
      for (const d of domains.slice(0, 10)) {
        try {
          const { data: cd } = await api.get(`/domains/contacts/${encodeURIComponent(d.name)}`);
          const reg = cd?.Registrant || {};
          if (!reg.FirstName && !reg.EmailAddress && !reg.LastName) continue;
          const merged = { ...emptyContact(), ...reg };
          setContacts({ Registrant: merged, Tech: merged, Admin: merged, AuxBilling: merged });
          setPrefillSource({ domain: d.name, created: d.created });
          return;
        } catch (_err) {
          // try next candidate
        }
      }
    } catch (_e) {
      // silent — prefill is optional
    } finally {
      setPrefillLoading(false);
    }
  };

  const clearContacts = () => {
    setContacts({
      Registrant: emptyContact(),
      Tech: emptyContact(),
      Admin: emptyContact(),
      AuxBilling: emptyContact(),
    });
    setPrefillSource(null);
  };

  const tld = useMemo(() => {
    const parts = (domain || "").split(".");
    return parts.length > 1 ? parts.slice(1).join(".") : "";
  }, [domain]);

  const totalPrice = useMemo(() => {
    if (!pricing) return null;
    return Number(pricing.price) * Number(years || 1);
  }, [pricing, years]);

  const canAfford = useMemo(() => {
    if (!balance || totalPrice == null) return null;
    const avail = Number(balance.AvailableBalance || balance.availableBalance || 0);
    return avail >= totalPrice;
  }, [balance, totalPrice]);

  const loadBalance = async () => {
    setBalanceLoading(true);
    try {
      const { data } = await api.get("/users/balances");
      setBalance(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Balance load failed");
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadPricing = async (tldVal) => {
    if (!tldVal) return;
    setPricingLoading(true);
    try {
      const { data } = await api.get("/users/pricing", {
        params: {
          product_type: "DOMAIN",
          product_category: "DOMAINS",
          action_name: "REGISTER",
          product_name: tldVal,
        },
      });
      setPricing(extractRegisterPrice(data, tldVal));
    } catch (e) {
      setPricing(null);
      toast.error(e?.response?.data?.detail || "Pricing load failed");
    } finally {
      setPricingLoading(false);
    }
  };

  const checkAvailability = async () => {
    if (!domain.includes(".")) {
      toast.error("Enter a valid domain (e.g., example.com)");
      return;
    }
    setChecking(true);
    setAvailability(null);
    try {
      const { data } = await api.post("/domains/check", { domains: [domain.trim().toLowerCase()] });
      const r = (data.results || [])[0] || null;
      setAvailability(r);
      if (r) {
        const av = r.Available === "true" || r.Available === true;
        if (av) toast.success(`${r.Domain} is available`);
        else toast.error(`${r.Domain} is not available`);
        if (tld) loadPricing(tld);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadBalance();
    loadLastContact();
    if (initial && initial.includes(".")) {
      checkAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload pricing if TLD changes (without availability)
  useEffect(() => {
    if (tld) loadPricing(tld);
    else setPricing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tld]);

  const propagate = (next) => {
    if (syncAll) {
      setContacts({ Registrant: next, Tech: next, Admin: next, AuxBilling: next });
    } else {
      setContacts((c) => ({ ...c, Registrant: next }));
    }
  };

  const buy = async () => {
    if (!domain.includes(".")) {
      toast.error("Enter a valid domain");
      return;
    }
    const av = availability?.Available === "true" || availability?.Available === true;
    if (!av) {
      toast.error("Domain is not available. Run a check first.");
      return;
    }
    const err = validateContact(contacts.Registrant);
    if (err) {
      toast.error(err);
      return;
    }
    if (!syncAll) {
      for (const role of ["Tech", "Admin", "AuxBilling"]) {
        const e2 = validateContact(contacts[role]);
        if (e2) {
          toast.error(`${role}: ${e2}`);
          return;
        }
      }
    }
    const nsArr = nameservers
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const contactPayload = syncAll
      ? { Registrant: contacts.Registrant, Tech: contacts.Registrant, Admin: contacts.Registrant, AuxBilling: contacts.Registrant }
      : contacts;

    setBuying(true);
    setReceipt(null);
    try {
      const { data } = await api.post("/domains/register", {
        domain_name: domain.trim().toLowerCase(),
        years: Number(years),
        contact: contactPayload,
        nameservers: nsArr,
      });
      setReceipt(data);
      toast.success(`${domain} registered`);
      // Refresh balance after purchase
      loadBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setBuying(false);
    }
  };

  const available = availability?.Available === "true" || availability?.Available === true;
  const premium = availability?.IsPremiumName === "true" || availability?.IsPremiumName === true;
  const premiumPrice = availability && Number(availability.PremiumRegistrationPrice || 0);

  const fmtMoney = (v, cur) => {
    if (v == null || Number.isNaN(Number(v))) return "—";
    const c = cur || balance?.Currency || pricing?.currency || "USD";
    return `${Number(v).toFixed(2)} ${c}`;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Purchase"
        title="Buy Domain"
        description="Register a domain and charge it to your Namecheap account balance."
        actions={
          <Button
            data-testid="btn-refresh-balance"
            variant="outline"
            onClick={loadBalance}
            disabled={balanceLoading}
            className="rounded-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${balanceLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />
      <Section>
        {/* Step 1: Domain & availability */}
        <Card className="rounded-sm shadow-none border-[var(--border)] mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Step 01 · Domain
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="buy-domain-input"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="mono rounded-sm"
                onKeyDown={(e) => e.key === "Enter" && checkAvailability()}
              />
              <Button
                data-testid="btn-check-availability"
                onClick={checkAvailability}
                disabled={checking}
                className="brand-bg hover:brand-bg text-white rounded-sm min-w-[160px]"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Check availability
              </Button>
            </div>

            {availability && (
              <div
                data-testid="availability-result"
                className="flex items-center justify-between px-4 py-3 border border-[var(--border)] rounded-sm"
              >
                <div className="flex items-center gap-3">
                  {available ? (
                    <Check className="w-4 h-4 text-[var(--success)]" />
                  ) : (
                    <X className="w-4 h-4 text-[var(--error)]" />
                  )}
                  <span className="mono text-sm">{availability.Domain}</span>
                  {premium && (
                    <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                      Premium
                    </Badge>
                  )}
                </div>
                <span
                  className={`text-xs uppercase tracking-wider mono ${
                    available ? "text-[var(--success)]" : "text-[var(--error)]"
                  }`}
                >
                  {available ? "Available" : "Taken"}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance + Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card data-testid="balance-card" className="rounded-sm shadow-none border-[var(--border)]">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                <Wallet className="w-3.5 h-3.5" /> Account Balance
              </div>
              {balanceLoading ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : balance ? (
                <div className="space-y-1.5">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-semibold tracking-tight mono">
                      {fmtMoney(balance.AvailableBalance ?? balance.availableBalance, balance.Currency)}
                    </div>
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mono space-y-0.5">
                    <div>
                      Account: {fmtMoney(balance.AccountBalance ?? balance.accountBalance, balance.Currency)}
                    </div>
                    {(balance.EarnedAmount || balance.earnedAmount) && (
                      <div>
                        Earned: {fmtMoney(balance.EarnedAmount ?? balance.earnedAmount, balance.Currency)}
                      </div>
                    )}
                    {(balance.WithdrawableAmount || balance.withdrawableAmount) && (
                      <div>
                        Withdrawable: {fmtMoney(balance.WithdrawableAmount ?? balance.withdrawableAmount, balance.Currency)}
                      </div>
                    )}
                    {(balance.FundsRequiredForAutoRenew || balance.fundsRequiredForAutoRenew) && (
                      <div>
                        For auto-renew: {fmtMoney(balance.FundsRequiredForAutoRenew ?? balance.fundsRequiredForAutoRenew, balance.Currency)}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[var(--text-secondary)]">Balance unavailable</div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="pricing-card" className="rounded-sm shadow-none border-[var(--border)]">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                <Tag className="w-3.5 h-3.5" /> Price{tld ? ` · .${tld}` : ""}
              </div>
              {pricingLoading ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : pricing ? (
                <div className="space-y-1.5">
                  <div className="text-3xl font-semibold tracking-tight mono">
                    {fmtMoney(pricing.price, pricing.currency)}
                    <span className="text-sm text-[var(--text-secondary)] ml-2">/ year</span>
                  </div>
                  {pricing.regular > pricing.price && (
                    <div className="text-xs mono text-[var(--text-secondary)]">
                      Regular: <span className="line-through">{fmtMoney(pricing.regular, pricing.currency)}</span>
                    </div>
                  )}
                  {premium && premiumPrice > 0 && (
                    <div className="text-xs mono text-amber-700">
                      Premium price: {fmtMoney(premiumPrice, pricing.currency)} (overrides standard)
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-[var(--text-secondary)]">
                  {tld ? "No pricing data" : "Enter a domain to see pricing"}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Step 2: Years + nameservers */}
        <Card className="rounded-sm shadow-none border-[var(--border)] mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Step 02 · Options
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6 sm:col-span-3">
                <Label className="text-xs uppercase tracking-wider">Years</Label>
                <Input
                  data-testid="buy-years"
                  type="number"
                  min={1}
                  max={10}
                  value={years}
                  onChange={(e) => setYears(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                  className="mono mt-1.5 rounded-sm h-9"
                />
              </div>
              <div className="col-span-12 sm:col-span-9">
                <Label className="text-xs uppercase tracking-wider">Nameservers (optional, comma-separated)</Label>
                <Input
                  data-testid="buy-nameservers"
                  value={nameservers}
                  onChange={(e) => setNameservers(e.target.value)}
                  placeholder="dns1.registrar-servers.com, dns2.registrar-servers.com"
                  className="mono mt-1.5 rounded-sm h-9"
                />
              </div>
            </div>
            {pricing && totalPrice != null && (
              <Alert className="rounded-sm border-[var(--border)]">
                <Info className="w-4 h-4" />
                <AlertDescription className="mono text-sm">
                  Estimated total: <strong>{fmtMoney(totalPrice, pricing.currency)}</strong>
                  {balance && canAfford === false && (
                    <span className="text-[var(--error)] ml-2">
                      · insufficient balance ({fmtMoney(balance.AvailableBalance, balance.Currency)})
                    </span>
                  )}
                  {balance && canAfford === true && (
                    <span className="text-[var(--success)] ml-2">· balance covers this purchase</span>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Contacts */}
        <Card className="rounded-sm shadow-none border-[var(--border)] mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                Step 03 · Registrant
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="sync-all-switch"
                  checked={syncAll}
                  onCheckedChange={setSyncAll}
                />
                <span className="text-xs text-[var(--text-secondary)]">
                  Use same contact for Tech / Admin / AuxBilling
                </span>
              </div>
            </div>

            {/* Copy WHOIS from a chosen domain */}
            <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-subtle,#fafafa)] p-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                <History className="w-4 h-4" />
                Copy WHOIS from
              </div>
              <Select
                value={prefillSource?.domain || ""}
                onValueChange={(v) => applyContactFromDomain(v)}
                onOpenChange={(open) => {
                  if (open) fetchOwnedDomains(); // live-refresh each time the dropdown is opened
                }}
                disabled={prefillLoading}
              >
                <SelectTrigger
                  data-testid="copy-whois-select"
                  className="rounded-sm h-9 mono text-xs w-[320px] bg-white"
                >
                  <SelectValue
                    placeholder={
                      domainsLoading
                        ? "Loading your domains…"
                        : ownedDomains.length === 0
                        ? "No domains found — click to load"
                        : `Pick a domain… (${ownedDomains.length} available)`
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {domainsLoading && (
                    <div className="px-2 py-3 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Refreshing list…
                    </div>
                  )}
                  {!domainsLoading && ownedDomains.length === 0 && (
                    <div className="px-2 py-3 text-xs text-[var(--text-secondary)]">
                      No domains in your account
                    </div>
                  )}
                  {!domainsLoading &&
                    ownedDomains.map((d) => (
                      <SelectItem
                        key={d.name}
                        value={d.name}
                        data-testid={`copy-whois-option-${d.name}`}
                        className="mono text-xs"
                      >
                        <span className="flex items-center justify-between w-full gap-3">
                          <span>{d.name}</span>
                          {d.created && (
                            <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">
                              {d.created}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="btn-refresh-domains"
                size="sm"
                variant="ghost"
                className="rounded-sm h-9 w-9 p-0"
                onClick={fetchOwnedDomains}
                disabled={domainsLoading}
                title="Refresh domain list"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${domainsLoading ? "animate-spin" : ""}`} />
              </Button>
              {prefillSource?.domain && (
                <Badge
                  variant="outline"
                  className="rounded-sm text-[10px] uppercase tracking-wider"
                  data-testid="prefill-badge"
                >
                  Active · {prefillSource.domain}
                </Badge>
              )}
              <div className="flex gap-1 ml-auto">
                <Button
                  data-testid="btn-reload-last-contact"
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-8 text-xs"
                  onClick={loadLastContact}
                  disabled={prefillLoading}
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${prefillLoading ? "animate-spin" : ""}`} />
                  Auto (newest)
                </Button>
                <Button
                  data-testid="btn-clear-contacts"
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-8 text-xs"
                  onClick={clearContacts}
                >
                  Clear
                </Button>
              </div>
            </div>

            {prefillLoading && !prefillSource && (
              <Alert className="rounded-sm border-[var(--border)]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <AlertDescription className="text-xs mono">
                  Loading WHOIS from your most recent domain…
                </AlertDescription>
              </Alert>
            )}

            {prefillSource && (
              <Alert
                data-testid="prefill-banner"
                className="rounded-sm border-[var(--border)] bg-transparent py-2"
              >
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-xs mono">
                  Registrant prefilled from <strong>{prefillSource.domain}</strong>
                  {prefillSource.created && (
                    <span className="text-[var(--text-secondary)] ml-2">
                      · registered {prefillSource.created}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <ContactForm
              value={contacts.Registrant}
              onChange={propagate}
              idPrefix="buy-Registrant"
            />

            {!syncAll && (
              <div className="space-y-6 pt-4 border-t border-[var(--border)]">
                {["Tech", "Admin", "AuxBilling"].map((role) => (
                  <div key={role}>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-3">
                      {role}
                    </div>
                    <ContactForm
                      value={contacts[role]}
                      onChange={(v) => setContacts((c) => ({ ...c, [role]: v }))}
                      idPrefix={`buy-${role}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 4: Buy */}
        <Card className="rounded-sm shadow-none border-[var(--border)] mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Step 04 · Confirm purchase
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="btn-buy"
                onClick={buy}
                disabled={buying || !available}
                className="brand-bg hover:brand-bg text-white rounded-sm min-w-[200px]"
              >
                {buying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2" /> Buy with balance
                  </>
                )}
              </Button>
              {!available && availability && (
                <span className="text-xs text-[var(--error)] mono">
                  Domain unavailable — cannot purchase
                </span>
              )}
              {!availability && (
                <span className="text-xs text-[var(--text-secondary)] mono">
                  Run availability check first
                </span>
              )}
            </div>

            {receipt && (
              <Alert data-testid="receipt" className="rounded-sm border-green-200 bg-green-50">
                <CheckCircle2 className="w-4 h-4 text-[var(--success)]" />
                <AlertDescription>
                  <div className="font-medium mb-2 mono">Purchase successful</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mono">
                    <div>Domain</div>
                    <div>{receipt.Domain || domain}</div>
                    <div>Registered</div>
                    <div>{String(receipt.Registered ?? "true")}</div>
                    {receipt.ChargedAmount && (
                      <>
                        <div>Charged (Namecheap)</div>
                        <div>{receipt.ChargedAmount}</div>
                      </>
                    )}
                    {receipt.charged != null && (
                      <>
                        <div>Charged (from balance)</div>
                        <div>{fmtMoney(receipt.charged, balance?.Currency)}</div>
                      </>
                    )}
                    {receipt.OrderID && (
                      <>
                        <div>Order ID</div>
                        <div>{receipt.OrderID}</div>
                      </>
                    )}
                    {receipt.TransactionID && (
                      <>
                        <div>Transaction ID</div>
                        <div>{receipt.TransactionID}</div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => navigate("/history")}
                    >
                      View history
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => navigate("/domains")}
                    >
                      My domains
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </Section>
    </div>
  );
};

export default BuyDomain;
